import { ResourceType } from '../../../graphQL';

export const BEACON_RESOURCE_METADATA: Partial<
  Record<ResourceType, { title: string; description: string }>
> = {
  [ResourceType.CampusCounseling]: {
    title: 'Campus counseling or wellness center(s)',
    description:
      'Resources shown to students experiencing psychological distress, mental/emotional health challenges, low motivation to complete degree, or personal safety concerns.',
  },
  [ResourceType.ThirdPartyClinical]: {
    title: 'Third-party clinical mental health services',
    description:
      'Off-campus or virtual clinical services like Mantra Health, shown for psychological distress and other mental health challenges.',
  },
  [ResourceType.OtherCommunityMentalHealth]: {
    title: 'Other community mental health providers or resources',
    description: 'Any other community mental health or wellness providers or resources.',
  },
  [ResourceType.CampusHealthCenter]: {
    title: 'Campus health center',
    description: 'On-campus resource(s) for students with physical health concerns.',
  },
  [ResourceType.LocalHospitals]: {
    title: 'Local hospital(s)',
    description: 'Local hospital or clinics for students with physical health concerns.',
  },
  [ResourceType.UninsuredResources]: {
    title: 'Campus or local resources for students without health insurance',
    description: 'Resources specifically for uninsured students with physical health needs.',
  },
  [ResourceType.OtherPhysicalHealth]: {
    title: 'Other physical health resources',
    description: 'Additional physical health resources and services available to students.',
  },
  [ResourceType.CampusClubs]: {
    title: 'Campus clubs, student orgs, or groups',
    description:
      'General resources for students with low sense of social fit to find community and belonging.',
  },
  [ResourceType.CampusActivityCalendar]: {
    title: 'Campus social calendar, calendar of group classes/activities',
    description:
      'Any link with an aggregated list of events and activities to help students build social connections and belonging.',
  },
  [ResourceType.CulturalCenters]: {
    title: 'Cultural centers and identity-specific centers',
    description:
      'Identity-based centers and resources for students seeking community and belonging.',
  },
  [ResourceType.MentorPrograms]: {
    title: 'Any mentoring or peer mentor programs available',
    description: 'Mentoring programs for social connection and academic motivation.',
  },
  [ResourceType.CoachingPrograms]: {
    title: 'Coaching programs',
    description:
      'Coaching resources (Mantra or campus-provided) for students needing social connection support.',
  },
  [ResourceType.OtherBelongingness]: {
    title: 'Other belongingness resources',
    description:
      'Additional resources for belonging and engagement, including cohort-specific programs.',
  },
  [ResourceType.FinancialAid]: {
    title: 'Financial aid',
    description:
      'Financial aid resources for students experiencing financial strain or financial barriers to degree completion.',
  },
  [ResourceType.StudentEmployment]: {
    title: 'Student employment resources',
    description: 'Student employment opportunities as financial support for students in need.',
  },
  [ResourceType.CareerServices]: {
    title: 'Career services',
    description:
      'Career counseling, resume help, and career path clarification for financial challenges and career opportunity concerns.',
  },
  [ResourceType.FinancialLiteracy]: {
    title: 'Financial literacy resources or programs',
    description:
      'Programs and resources to help students develop financial management skills, shown to students who demonstrate signs of financial strain.',
  },
  [ResourceType.EmergencyFunding]: {
    title: 'Campus student emergency funding',
    description:
      'Emergency financial assistance available to students who demonstrate signs of financial strain.',
  },
  [ResourceType.OtherFinancial]: {
    title: 'Other financial resources',
    description:
      'Additional financial resources and assistance programs for students who demonstrate signs of financial strain.',
  },
  [ResourceType.CaseManager]: {
    title: 'Campus social worker or case manager',
    description:
      'Professional support for students with financial, family, and work-related barriers to education.',
  },
  [ResourceType.OtherFamilyAssistance]: {
    title: 'Other family assistance programs or resources',
    description:
      'Resources for students struggling with family obligations and childcare challenges.',
  },
  [ResourceType.AcademicAdvising]: {
    title: 'Academic Advising',
    description:
      'Academic support for students with academic challenges, stress, low motivation, or other barrier to completing their degree.',
  },
  [ResourceType.TutoringServices]: {
    title: 'Tutoring Services',
    description: 'Tutoring support for students experiencing academic challenges and stress.',
  },
  [ResourceType.OtherAcademic]: {
    title: 'Other academic programs or resources',
    description:
      'Additional academic resources and programs, including Mantra Productivity Coaching if eligible.',
  },
  [ResourceType.DvShelter]: {
    title: 'Local DV shelter or support center',
    description:
      'Local domestic violence resources for students experiencing relationship difficulties or safety concerns.',
  },
  [ResourceType.OtherPersonalSafety]: {
    title: 'Other Personal Safety resources',
    description: 'Resources for students facing personal safety concerns or crises.',
  },
  [ResourceType.LocalFoodPantry]: {
    title: 'Campus and local food pantries',
    description:
      'Food assistance for students experiencing food insecurity or having difficulty affording food.',
  },
  [ResourceType.LocalFoodAssistance]: {
    title: 'Other local food assistance programs',
    description: 'Additional local food programs for students with food insecurity needs.',
  },
  [ResourceType.AffordableDining]: {
    title: 'Affordable campus dining options',
    description:
      'Budget-friendly dining options on campus for students with food security concerns.',
  },
  [ResourceType.Nutritionist]: {
    title: 'Campus or local nutritionist',
    description: 'Nutritional support and guidance for students with food and nutrition concerns.',
  },
  [ResourceType.LocalShelters]: {
    title: 'Local shelters',
    description: 'Local housing resources for students experiencing housing insecurity.',
  },
  [ResourceType.OtherHousing]: {
    title: 'Other local housing resources',
    description: 'Additional housing assistance and resources for students with housing needs.',
  },
  [ResourceType.LocalHousingAuthority]: {
    title: 'Local Housing Authority',
    description:
      'Local housing authority resources for students with poor living conditions impacting their well-being.',
  },
  [ResourceType.OtherPoorLivingConditions]: {
    title: 'Other resources for poor living conditions',
    description:
      'Resources for students experiencing poor living conditions impacting their well-being.',
  },
  [ResourceType.TransportationAid]: {
    title: 'Transportation Aid Program(s)',
    description: 'Transportation assistance for students facing transportation challenges.',
  },
  [ResourceType.LocalChildcareResources]: {
    title: 'Campus or local childcare or caregiving resources',
    description: 'Childcare and caregiving support for students with family care responsibilities.',
  },
  [ResourceType.CampusSafety]: {
    title: 'Campus safety and residence life',
    description:
      'Campus security escorts, residence life staff, hall director support, and other campus safety resources for students with physical or emotional safety concerns.',
  },
  [ResourceType.OnCampusHousing]: {
    title: 'On-campus housing resources',
    description:
      'On-campus residence life housing support, emergency campus housing, or housing office resources for students with housing insecurity or poor living conditions.',
  },
  [ResourceType.SleepAndWellness]: {
    title: 'Sleep and wellness programs',
    description:
      'Campus sleep workshops, CBT-I (cognitive behavioral therapy for insomnia) programs, sleep hygiene resources, and wellness programs for students experiencing sleep disruption.',
  },
  [ResourceType.StressManagement]: {
    title: 'Stress management and wellness programs',
    description:
      'Mindfulness programs, stress management workshops, peer wellness groups, and non-clinical wellness resources for students experiencing elevated stress.',
  },
  [ResourceType.StudentSuccessCoaching]: {
    title: 'Academic coaching or success programs',
    description:
      'Structured academic coaching, student success programs, study skills coaching, and productivity coaching (including Mantra Productivity Coaching) for students with low academic confidence or motivation.',
  },
};

export const BEACON_RESOURCE_TYPES = Object.keys(BEACON_RESOURCE_METADATA) as ResourceType[];

export const isBeaconResource = (resourceType: ResourceType): boolean => {
  return resourceType in BEACON_RESOURCE_METADATA;
};
